import React, { useEffect, useState, useCallback, useRef } from 'react';
import styled from 'styled-components';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from 'chart.js';
import { Line, Bar } from 'react-chartjs-2';
import { exportMarketingAnalyticsPdf } from '../../../utils/exportMarketingAnalyticsPdf';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

// ── API ───────────────────────────────────────────────────────────────────────
function getApiBase() {
  const env = process.env.REACT_APP_ENV;
  if (env === 'LOCAL')   return 'http://localhost:8888/Website/front-end/public/api';
  if (env === 'STAGING') return 'https://staging.belvoircastle.com/api';
  return 'https://www.belvoircastle.com/api';
}

// ── Styled components (match Security Centre card system) ─────────────────────

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const ToolbarRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex-wrap: wrap;
`;

const ToolbarLabel = styled.span`
  font-size: 0.9rem;
  font-weight: 600;
  color: ${p => p.theme.colors.secondary};
`;

const RangeButton = styled.button`
  padding: 0.4rem 1.1rem;
  border-radius: 8px;
  border: 2px solid ${p => p.active ? p.theme.colors.primary : 'rgba(16,29,64,0.15)'};
  background: ${p => p.active ? p.theme.colors.primary : 'white'};
  color: ${p => p.active ? 'white' : p.theme.colors.primary};
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    border-color: ${p => p.theme.colors.primary};
    background: ${p => p.active ? p.theme.colors.secondary : 'rgba(16,29,64,0.05)'};
  }
`;

const Select = styled.select`
  padding: 0.4rem 0.75rem;
  border-radius: 8px;
  border: 2px solid rgba(16,29,64,0.15);
  background: white;
  color: ${p => p.theme.colors.primary};
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;

  &:focus {
    outline: none;
    border-color: ${p => p.theme.colors.primary};
  }
`;

const ExportButton = styled.button`
  margin-left: auto;
  padding: 0.55rem 1.25rem;
  border-radius: 8px;
  border: none;
  background: #101d40;
  color: #c9a24b;
  font-size: 0.8rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  cursor: pointer;
  transition: background 0.2s, transform 0.15s;

  &:hover:not(:disabled) {
    background: #1a3060;
    transform: translateY(-1px);
  }

  &:disabled {
    opacity: 0.45;
    cursor: not-allowed;
  }
`;

function getChartImage(ref) {
  try {
    let chart = ref?.current;
    // react-chartjs-2 may expose the Chart.js instance directly or via .chart
    if (chart && typeof chart.toBase64Image !== 'function' && chart.chart) {
      chart = chart.chart;
    }
    if (!chart || typeof chart.toBase64Image !== 'function') return null;
    return chart.toBase64Image('image/png', 1);
  } catch {
    return null;
  }
}

const KpiGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(160px, 1fr));
  gap: 1rem;
`;

const KpiCard = styled.div`
  background: white;
  border: 1px solid rgba(16,29,64,0.08);
  border-radius: 12px;
  padding: 1.1rem 1.25rem 1rem;
  box-shadow: 0 2px 8px rgba(0,0,0,0.04);
`;

const KpiLabel = styled.p`
  margin: 0 0 0.3rem;
  font-size: 0.78rem;
  font-weight: 600;
  color: ${p => p.theme.colors.secondary};
  text-transform: uppercase;
  letter-spacing: 0.04em;
`;

const KpiValue = styled.p`
  margin: 0;
  font-size: 1.9rem;
  font-weight: 700;
  color: ${p => p.theme.colors.primary};
  line-height: 1;
`;

const KpiSub = styled.p`
  margin: 0.25rem 0 0;
  font-size: 0.78rem;
  color: ${p => p.theme.colors.secondary};
  opacity: 0.7;
`;

const ChartsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1.5rem;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

const ChartCard = styled.div`
  background: white;
  border: 1px solid rgba(16,29,64,0.08);
  border-radius: 14px;
  padding: 1.5rem;
  box-shadow: 0 2px 8px rgba(0,0,0,0.04);
`;

const ChartTitle = styled.h3`
  margin: 0 0 1.25rem;
  font-size: 0.95rem;
  font-weight: 700;
  color: ${p => p.theme.colors.primary};
`;

const TableCard = styled(ChartCard)`
  grid-column: 1 / -1;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 0.85rem;
`;

const Th = styled.th`
  text-align: left;
  padding: 0.6rem 0.75rem;
  background: ${p => p.theme.colors.offWhite};
  color: ${p => p.theme.colors.secondary};
  font-size: 0.78rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  border-bottom: 1px solid rgba(16,29,64,0.1);

  &:first-child { border-radius: 8px 0 0 8px; }
  &:last-child  { border-radius: 0 8px 8px 0; }
`;

const Td = styled.td`
  padding: 0.65rem 0.75rem;
  border-bottom: 1px solid rgba(16,29,64,0.06);
  color: ${p => p.theme.colors.primary};
  vertical-align: middle;
`;

const TypeBadge = styled.span`
  display: inline-block;
  padding: 0.2rem 0.65rem;
  border-radius: 20px;
  font-size: 0.75rem;
  font-weight: 600;
  background: ${p => p.type === 'membership_signup'
    ? 'rgba(157,109,73,0.12)'
    : 'rgba(16,29,64,0.08)'};
  color: ${p => p.type === 'membership_signup'
    ? '#9d6d49'
    : '#101d40'};
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 3rem;
  color: ${p => p.theme.colors.secondary};
  opacity: 0.6;
  font-size: 0.95rem;
`;

const PaginationRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 0.4rem;
  margin-top: 0.9rem;
  font-size: 0.82rem;
  color: ${p => p.theme.colors.secondary};
`;

const PageBtn = styled.button`
  padding: 0.3rem 0.7rem;
  border-radius: 6px;
  border: 1.5px solid ${p => p.active ? p.theme.colors.primary : 'rgba(16,29,64,0.18)'};
  background: ${p => p.active ? p.theme.colors.primary : 'white'};
  color: ${p => p.active ? 'white' : p.theme.colors.primary};
  font-size: 0.8rem;
  font-weight: 600;
  cursor: ${p => p.disabled ? 'default' : 'pointer'};
  opacity: ${p => p.disabled ? 0.35 : 1};
  transition: all 0.15s;

  &:hover:not(:disabled):not([data-active='true']) {
    background: rgba(16,29,64,0.06);
  }
`;

const FormInput = styled.input`
  padding: 0.5rem 0.75rem;
  border: 1.5px solid rgba(16,29,64,0.18);
  border-radius: 8px;
  font-size: 0.88rem;
  color: ${p => p.theme.colors.primary};
  background: white;
  width: 100%;
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: ${p => p.theme.colors.primary};
  }
`;

const ErrorBox = styled.div`
  background: #fff5f5;
  border: 1px solid #feb2b2;
  border-radius: 10px;
  padding: 1rem 1.25rem;
  color: #c53030;
  font-size: 0.9rem;
`;

const Spinner = styled.div`
  text-align: center;
  padding: 3rem;
  color: ${p => p.theme.colors.secondary};
  font-size: 0.95rem;
`;

// ── Chart config helpers ──────────────────────────────────────────────────────
const NAVY   = '#101d40';
const ACCENT = '#9d6d49';
const TEAL   = '#2a7f8f';

const lineDefaults = {
  tension: 0.35,
  pointRadius: 2,
  pointHoverRadius: 4,
  borderWidth: 2,
  fill: true,
};

const chartOptions = (title) => ({
  responsive: true,
  plugins: {
    legend: { display: true, labels: { boxWidth: 12, font: { size: 11 } } },
    title:  { display: false },
    tooltip: { mode: 'index', intersect: false },
  },
  scales: {
    x: { grid: { display: false }, ticks: { font: { size: 10 } } },
    y: { beginAtZero: true, ticks: { font: { size: 10 } } },
  },
});

// ── Pagination helper ─────────────────────────────────────────────────────────
const PAGE_SIZE_CITIES = 20;
const PAGE_SIZE_LOC    = 30;
const PAGE_SIZE_CONV   = 25;
const PAGE_SIZE_CAMP   = 20;

function Pagination({ page, total, pageSize, onChange }) {
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  if (totalPages <= 1) return null;
  const from = page * pageSize + 1;
  const to   = Math.min((page + 1) * pageSize, total);
  return (
    <PaginationRow>
      <span>{from}–{to} of {total.toLocaleString()}</span>
      <PageBtn disabled={page === 0} onClick={() => onChange(page - 1)}>&lsaquo; Prev</PageBtn>
      {Array.from({ length: Math.min(totalPages, 7) }, (_, i) => {
        const p = totalPages <= 7 ? i : (
          page < 4 ? i :
          page > totalPages - 4 ? totalPages - 7 + i :
          page - 3 + i
        );
        return (
          <PageBtn key={p} data-active={p === page ? 'true' : 'false'} active={p === page} onClick={() => onChange(p)}>{p + 1}</PageBtn>
        );
      })}
      <PageBtn disabled={page >= totalPages - 1} onClick={() => onChange(page + 1)}>Next &rsaquo;</PageBtn>
    </PaginationRow>
  );
}

// ── Build date series (fill gaps) ─────────────────────────────────────────────
function buildDateRange(days) {
  const dates = [];
  for (let i = days - 1; i >= 0; i--) {
    const d = new Date();
    d.setDate(d.getDate() - i);
    dates.push(d.toISOString().slice(0, 10));
  }
  return dates;
}

function fillSeries(rows, dateField, valueField, dates) {
  const map = {};
  rows.forEach(r => { map[r[dateField]] = Number(r[valueField] ?? 0); });
  return dates.map(d => map[d] ?? 0);
}

function formatCurrency(val) {
  if (val === null || val === undefined) return '—';
  return new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP', minimumFractionDigits: 0 }).format(val);
}

function formatDate(str) {
  if (!str) return '—';
  return new Date(str).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

function formatDateShort(str) {
  if (!str) return '';
  const d = new Date(str);
  return `${d.getDate()} ${d.toLocaleString('en-GB', { month: 'short' })}`;
}

/** Campaign name with Meta ID underneath when resolved */
function CampaignCell({ name, id, raw }) {
  const label = name || raw || '';
  if (!label) return '—';
  const showId = id && id !== label;
  return (
    <>
      <span style={{ fontWeight: 600 }}>{label}</span>
      {showId && (
        <div style={{ fontSize: '0.7rem', opacity: 0.5, fontFamily: 'monospace', marginTop: 1 }}>{id}</div>
      )}
    </>
  );
}

// ── Component ─────────────────────────────────────────────────────────────────
const MarketingAnalytics = () => {
  const [days, setDays]               = useState(30);
  const [data, setData]               = useState(null);
  const [loading, setLoading]         = useState(false);
  const [error, setError]             = useState(null);
  const [regionFilter, setRegionFilter] = useState('all');
  const [locationSort, setLocationSort] = useState('conversions');
  const [exporting, setExporting]     = useState(false);

  // pagination state
  const [citiesPage, setCitiesPage]   = useState(0);
  const [locPage, setLocPage]         = useState(0);
  const [convPage, setConvPage]       = useState(0);
  const [campPage, setCampPage]       = useState(0);
  const [campSearch, setCampSearch]   = useState('');

  const sessionsChartRef = useRef(null);
  const conversionsChartRef = useRef(null);
  const sourcesChartRef = useRef(null);
  const firstTouchChartRef = useRef(null);
  const citiesChartRef = useRef(null);

  const fetchData = useCallback(async (d) => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(
        `${getApiBase()}/marketing-analytics.php?action=report&days=${d}`,
        { headers: { 'X-Admin-Request': '1' } }
      );
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json = await res.json();
      if (!json.success) throw new Error(json.error || 'Unknown error');
      setData(json);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(days); }, [days, fetchData]);

  const handleRange = (d) => { setDays(d); };

  const handleExportPdf = () => {
    if (!data) return;
    setExporting(true);
    try {
      // Capture charts after a brief paint so canvases are ready
      const allLoc = data.location_referral || [];
      const exportLocRows = allLoc
        .filter(r => regionFilter === 'all' || r.region === regionFilter)
        .slice()
        .sort((a, b) => (b.conversions || 0) - (a.conversions || 0));

      exportMarketingAnalyticsPdf({
        data,
        days,
        chartImages: {
          sessions: getChartImage(sessionsChartRef),
          conversions: getChartImage(conversionsChartRef),
          sources: getChartImage(sourcesChartRef),
          firstTouch: getChartImage(firstTouchChartRef),
          cities: getChartImage(citiesChartRef),
          regionChannels: null,
        },
        bestPerRegion: data.best_per_region || [],
        topCities: data.cities || [],
        locationRows: exportLocRows,
      });
    } catch (e) {
      alert(e.message || 'Failed to open PDF export');
    } finally {
      setExporting(false);
    }
  };

  // ── Build chart datasets ──────────────────────────────────────────────────
  let sessionChart         = null;
  let conversionChart      = null;
  let sourceChart          = null;
  let firstTouchChart      = null;
  let citiesChart          = null;
  let locationRows         = [];
  let regionOptions        = [];
  let bestPerRegionRows    = [];

  if (data) {
    const dates = buildDateRange(days);
    const labels = dates.map(formatDateShort);

    // Sessions over time
    sessionChart = {
      labels,
      datasets: [
        {
          ...lineDefaults,
          label: 'Sessions',
          data:  fillSeries(data.daily_sessions, 'date', 'sessions', dates),
          borderColor: NAVY,
          backgroundColor: 'rgba(16,29,64,0.07)',
        },
        {
          ...lineDefaults,
          label: 'Unique Visitors',
          data:  fillSeries(data.daily_sessions, 'date', 'visitors', dates),
          borderColor: TEAL,
          backgroundColor: 'rgba(42,127,143,0.06)',
        },
      ],
    };

    // Conversions over time
    const ticketByDate = {};
    const memberByDate = {};
    data.daily_conversions.forEach(r => {
      if (r.type === 'ticket_booking')    ticketByDate[r.date] = Number(r.cnt);
      if (r.type === 'membership_signup') memberByDate[r.date] = Number(r.cnt);
    });
    conversionChart = {
      labels,
      datasets: [
        {
          ...lineDefaults,
          label: 'Ticket Bookings',
          data:  dates.map(d => ticketByDate[d] ?? 0),
          borderColor: ACCENT,
          backgroundColor: 'rgba(157,109,73,0.08)',
        },
        {
          ...lineDefaults,
          label: 'Memberships',
          data:  dates.map(d => memberByDate[d] ?? 0),
          borderColor: NAVY,
          backgroundColor: 'rgba(16,29,64,0.07)',
        },
      ],
    };

    // Last-touch traffic sources (bar — top 8)
    const topSources = [...data.sources].slice(0, 8);
    sourceChart = {
      labels: topSources.map(s => s.utm_medium ? `${s.source} / ${s.utm_medium}` : s.source),
      datasets: [{
        label: 'Sessions',
        data:  topSources.map(s => Number(s.sessions)),
        backgroundColor: topSources.map((_, i) =>
          i === 0 ? NAVY : i === 1 ? ACCENT : i === 2 ? TEAL : `rgba(16,29,64,${0.45 - i * 0.04})`
        ),
        borderRadius: 6,
      }],
    };

    // First-touch attribution — where buyers came from originally
    const topFT = [...(data.first_touch_sources || [])].slice(0, 8);
    if (topFT.length > 0) {
      firstTouchChart = {
        labels: topFT.map(s => s.first_touch_medium ? `${s.first_touch_source} / ${s.first_touch_medium}` : s.first_touch_source),
        datasets: [{
          label: 'Conversions',
          data:  topFT.map(s => Number(s.conversions)),
          backgroundColor: topFT.map((_, i) =>
            i === 0 ? ACCENT : i === 1 ? NAVY : i === 2 ? TEAL : `rgba(157,109,73,${0.75 - i * 0.07})`
          ),
          borderRadius: 6,
        }],
      };
    }

    // Towns & cities (top 15 by sessions)
    const topCities = [...(data.cities || [])].slice(0, 15);
    if (topCities.length > 0) {
      citiesChart = {
        labels: topCities.map(c => {
          const region = c.region && c.region !== c.city ? `, ${c.region}` : '';
          return `${c.city}${region}`;
        }),
        datasets: [{
          label: 'Sessions',
          data:  topCities.map(c => Number(c.sessions)),
          backgroundColor: topCities.map((_, i) =>
            i === 0 ? NAVY : i === 1 ? ACCENT : i === 2 ? TEAL : `rgba(16,29,64,${0.55 - i * 0.03})`
          ),
          borderRadius: 6,
        }],
      };
    }

    // Location × referral rows (filter + sort client-side)
    const allLoc = data.location_referral || [];
    regionOptions = [...new Set(allLoc.map(r => r.region).filter(Boolean))].sort();
    bestPerRegionRows = data.best_per_region || [];

    locationRows = allLoc
      .filter(r => regionFilter === 'all' || r.region === regionFilter)
      .slice();
    locationRows.sort((a, b) => {
      if (locationSort === 'revenue') return (b.revenue || 0) - (a.revenue || 0);
      if (locationSort === 'sessions') return (b.sessions || 0) - (a.sessions || 0);
      return (b.conversions || 0) - (a.conversions || 0);
    });
  }

  // ── Campaigns (from API, filtered by search) ──────────────────────────────
  const allCampaigns = data?.campaigns || [];
  const filteredCampaigns = campSearch.trim()
    ? allCampaigns.filter(c => {
        const q = campSearch.toLowerCase();
        return (c.name || '').toLowerCase().includes(q)
          || String(c.campaign_id || '').includes(campSearch)
          || String(c.utm_campaign || '').toLowerCase().includes(q);
      })
    : allCampaigns;
  const campPageRows = filteredCampaigns.slice(campPage * PAGE_SIZE_CAMP, (campPage + 1) * PAGE_SIZE_CAMP);

  // Paginated slices for other tables
  const citiesRows   = (data?.cities || []).slice(citiesPage * PAGE_SIZE_CITIES, (citiesPage + 1) * PAGE_SIZE_CITIES);
  const locPageRows  = locationRows.slice(locPage * PAGE_SIZE_LOC, (locPage + 1) * PAGE_SIZE_LOC);
  const convPageRows = (data?.recent_conversions || []).slice(convPage * PAGE_SIZE_CONV, (convPage + 1) * PAGE_SIZE_CONV);

  return (
    <Wrapper>
      <ToolbarRow>
        <ToolbarLabel>Period:</ToolbarLabel>
        {[7, 30, 90].map(d => (
          <RangeButton key={d} active={days === d} onClick={() => handleRange(d)}>
            {d} days
          </RangeButton>
        ))}
        <ExportButton
          type="button"
          disabled={!data || loading || exporting}
          onClick={handleExportPdf}
        >
          {exporting ? 'Preparing…' : 'Export PDF'}
        </ExportButton>
      </ToolbarRow>

      {loading && <Spinner>Loading analytics data…</Spinner>}
      {error && <ErrorBox>Failed to load data: {error}</ErrorBox>}

      {!loading && !error && data && (
        <>
          {/* KPI row */}
          <KpiGrid>
            <KpiCard>
              <KpiLabel>Unique Visitors</KpiLabel>
              <KpiValue>{data.kpis.unique_visitors.toLocaleString()}</KpiValue>
            </KpiCard>
            <KpiCard>
              <KpiLabel>Sessions</KpiLabel>
              <KpiValue>{data.kpis.total_sessions.toLocaleString()}</KpiValue>
            </KpiCard>
            <KpiCard>
              <KpiLabel>Returning Rate</KpiLabel>
              <KpiValue>{data.kpis.returning_rate}%</KpiValue>
              <KpiSub>{data.kpis.returning_sessions.toLocaleString()} returning sessions</KpiSub>
            </KpiCard>
            <KpiCard>
              <KpiLabel>Ticket Bookings</KpiLabel>
              <KpiValue>{data.kpis.ticket_bookings.toLocaleString()}</KpiValue>
            </KpiCard>
            <KpiCard>
              <KpiLabel>Memberships</KpiLabel>
              <KpiValue>{data.kpis.membership_signups.toLocaleString()}</KpiValue>
            </KpiCard>
            <KpiCard>
              <KpiLabel>Conversion Rate</KpiLabel>
              <KpiValue>{data.kpis.conversion_rate}%</KpiValue>
              <KpiSub>{data.kpis.total_conversions} total</KpiSub>
            </KpiCard>
            <KpiCard>
              <KpiLabel>Attributed Revenue</KpiLabel>
              <KpiValue style={{ fontSize: '1.5rem' }}>{formatCurrency(data.kpis.total_revenue)}</KpiValue>
            </KpiCard>
            <KpiCard>
              <KpiLabel>Avg Pages Before Buying</KpiLabel>
              <KpiValue>{data.kpis.avg_pages_before_conversion}</KpiValue>
              <KpiSub>pages in converting session</KpiSub>
            </KpiCard>
            <KpiCard>
              <KpiLabel>Avg Sessions to Convert</KpiLabel>
              <KpiValue>{data.kpis.avg_sessions_to_convert}</KpiValue>
              <KpiSub>visits before purchase</KpiSub>
            </KpiCard>
            <KpiCard>
              <KpiLabel>Returning Buyers</KpiLabel>
              <KpiValue>{data.kpis.returning_conversion_rate}%</KpiValue>
              <KpiSub>{data.kpis.returning_conversion_count} of {data.kpis.total_conversions} conversions</KpiSub>
            </KpiCard>
          </KpiGrid>

          {/* Charts */}
          <ChartsGrid>
            <ChartCard>
              <ChartTitle>Sessions over time</ChartTitle>
              {sessionChart ? (
                <Line ref={sessionsChartRef} data={sessionChart} options={chartOptions()} height={220} />
              ) : (
                <EmptyState>No session data yet</EmptyState>
              )}
            </ChartCard>

            <ChartCard>
              <ChartTitle>Conversions over time</ChartTitle>
              {conversionChart ? (
                <Line ref={conversionsChartRef} data={conversionChart} options={chartOptions()} height={220} />
              ) : (
                <EmptyState>No conversion data yet</EmptyState>
              )}
            </ChartCard>

            <ChartCard>
              <ChartTitle>Last-touch traffic sources (all sessions)</ChartTitle>
              {sourceChart && sourceChart.labels.length > 0 ? (
                <Bar
                  ref={sourcesChartRef}
                  data={sourceChart}
                  options={{
                    ...chartOptions(),
                    plugins: { ...chartOptions().plugins, legend: { display: false } },
                    indexAxis: 'y',
                  }}
                  height={220}
                />
              ) : (
                <EmptyState>No source data yet</EmptyState>
              )}
            </ChartCard>

            <ChartCard>
              <ChartTitle>First-touch attribution (where buyers originally came from)</ChartTitle>
              {firstTouchChart ? (
                <Bar
                  ref={firstTouchChartRef}
                  data={firstTouchChart}
                  options={{
                    ...chartOptions(),
                    plugins: { ...chartOptions().plugins, legend: { display: false } },
                    indexAxis: 'y',
                  }}
                  height={220}
                />
              ) : (
                <EmptyState>Not enough conversion data yet</EmptyState>
              )}
            </ChartCard>

            <ChartCard>
              <ChartTitle>Top landing pages</ChartTitle>
              {data.landing_pages.length > 0 ? (
                <Table>
                  <thead>
                    <tr>
                      <Th>Path</Th>
                      <Th style={{ textAlign: 'right' }}>Sessions</Th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.landing_pages.map((lp, i) => (
                      <tr key={i}>
                        <Td style={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>{lp.landing_path}</Td>
                        <Td style={{ textAlign: 'right', fontWeight: 600 }}>{Number(lp.sessions).toLocaleString()}</Td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              ) : (
                <EmptyState>No data yet</EmptyState>
              )}
            </ChartCard>

            {/* Towns & cities — full width for clarity */}
            <TableCard>
              <ChartTitle>Visitors by town / city</ChartTitle>
              <p style={{ margin: '0 0 1rem', fontSize: '0.82rem', color: '#1a3352', opacity: 0.7 }}>
                Approximate location from IP (city / town level). Not GPS — VPNs and mobile networks can appear in a nearby city.
              </p>
              {citiesChart ? (
                <>
                  <div style={{ maxWidth: 900, marginBottom: '1.25rem' }}>
                    <Bar
                      ref={citiesChartRef}
                      data={citiesChart}
                      options={{
                        ...chartOptions(),
                        plugins: { ...chartOptions().plugins, legend: { display: false } },
                        indexAxis: 'y',
                        maintainAspectRatio: false,
                      }}
                      height={Math.max(280, (citiesChart.labels.length || 1) * 28)}
                    />
                  </div>
                  <Table>
                    <thead>
                      <tr>
                        <Th>Town / City</Th>
                        <Th>Region</Th>
                        <Th>Country</Th>
                        <Th style={{ textAlign: 'right' }}>Sessions</Th>
                        <Th style={{ textAlign: 'right' }}>Visitors</Th>
                      </tr>
                    </thead>
                    <tbody>
                      {citiesRows.map((c, i) => (
                        <tr key={i}>
                          <Td style={{ fontWeight: 600 }}>{c.city}</Td>
                          <Td>{c.region || '—'}</Td>
                          <Td>{c.country || '—'}</Td>
                          <Td style={{ textAlign: 'right', fontWeight: 600 }}>{Number(c.sessions).toLocaleString()}</Td>
                          <Td style={{ textAlign: 'right' }}>{Number(c.visitors).toLocaleString()}</Td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                  <Pagination
                    page={citiesPage}
                    total={(data.cities || []).length}
                    pageSize={PAGE_SIZE_CITIES}
                    onChange={p => setCitiesPage(p)}
                  />
                </>
              ) : (
                <EmptyState>No location data yet — new sessions after deploy will appear here.</EmptyState>
              )}
            </TableCard>

            {/* Location × referral — ads / channels by region */}
            <TableCard>
              <ChartTitle>Location × referral — what’s working by region</ChartTitle>
              <p style={{ margin: '0 0 1rem', fontSize: '0.82rem', color: '#1a3352', opacity: 0.7 }}>
                Crosses approximate town/region with UTM source / medium / campaign (and organic referrers).
                Use this to see whether a particular ad or channel is stronger in specific regions.
                Attribution is <strong>last-touch</strong> on the visiting / booking session.
              </p>

              <ToolbarRow style={{ marginBottom: '1.25rem' }}>
                <ToolbarLabel>Region:</ToolbarLabel>
                <Select value={regionFilter} onChange={e => setRegionFilter(e.target.value)}>
                  <option value="all">All regions</option>
                  {regionOptions.map(r => (
                    <option key={r} value={r}>{r}</option>
                  ))}
                </Select>
                <ToolbarLabel style={{ marginLeft: '0.5rem' }}>Sort by:</ToolbarLabel>
                <Select value={locationSort} onChange={e => setLocationSort(e.target.value)}>
                  <option value="conversions">Conversions</option>
                  <option value="revenue">Revenue</option>
                  <option value="sessions">Sessions</option>
                </Select>
              </ToolbarRow>

              {bestPerRegionRows.length > 0 && regionFilter === 'all' && (
                <>
                  <ChartTitle style={{ fontSize: '0.88rem', marginBottom: '0.75rem' }}>
                    Most effective channel per region
                  </ChartTitle>
                  <div style={{ overflowX: 'auto', marginBottom: '1.5rem' }}>
                    <Table>
                      <thead>
                        <tr>
                          <Th>Region</Th>
                          <Th>Best source</Th>
                          <Th>Medium</Th>
                          <Th>Campaign</Th>
                          <Th style={{ textAlign: 'right' }}>Sessions</Th>
                          <Th style={{ textAlign: 'right' }}>Conversions</Th>
                          <Th style={{ textAlign: 'right' }}>Conv. rate</Th>
                          <Th style={{ textAlign: 'right' }}>Revenue</Th>
                        </tr>
                      </thead>
                      <tbody>
                        {bestPerRegionRows.slice(0, 20).map((r, i) => (
                          <tr key={i}>
                            <Td style={{ fontWeight: 600 }}>{r.region}</Td>
                            <Td>{r.source || '—'}</Td>
                            <Td>{r.medium || '—'}</Td>
                            <Td>
                              <CampaignCell name={r.campaign} id={r.campaign_id} raw={r.campaign_raw} />
                            </Td>
                            <Td style={{ textAlign: 'right' }}>{Number(r.sessions).toLocaleString()}</Td>
                            <Td style={{ textAlign: 'right', fontWeight: 600 }}>{Number(r.conversions).toLocaleString()}</Td>
                            <Td style={{ textAlign: 'right' }}>{r.conversion_rate}%</Td>
                            <Td style={{ textAlign: 'right' }}>{formatCurrency(r.revenue)}</Td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                </>
              )}

              <ChartTitle style={{ fontSize: '0.88rem', marginBottom: '0.75rem' }}>
                Full breakdown (region × city × channel)
              </ChartTitle>
              {locationRows.length > 0 ? (
                <div style={{ overflowX: 'auto' }}>
                  <Table>
                    <thead>
                      <tr>
                        <Th>Region</Th>
                        <Th>Town / City</Th>
                        <Th>Source</Th>
                        <Th>Medium</Th>
                        <Th>Campaign</Th>
                        <Th style={{ textAlign: 'right' }}>Sessions</Th>
                        <Th style={{ textAlign: 'right' }}>Conversions</Th>
                        <Th style={{ textAlign: 'right' }}>Conv. rate</Th>
                        <Th style={{ textAlign: 'right' }}>Revenue</Th>
                      </tr>
                    </thead>
                    <tbody>
                      {locPageRows.map((r, i) => (
                        <tr key={i}>
                          <Td>{r.region}</Td>
                          <Td style={{ fontWeight: 600 }}>{r.city}</Td>
                          <Td>{r.source || '—'}</Td>
                          <Td>{r.medium || '—'}</Td>
                          <Td>
                            <CampaignCell name={r.campaign} id={r.campaign_id} raw={r.campaign_raw} />
                          </Td>
                          <Td style={{ textAlign: 'right' }}>{Number(r.sessions).toLocaleString()}</Td>
                          <Td style={{ textAlign: 'right', fontWeight: 600 }}>{Number(r.conversions).toLocaleString()}</Td>
                          <Td style={{ textAlign: 'right' }}>{r.conversion_rate}%</Td>
                          <Td style={{ textAlign: 'right' }}>{formatCurrency(r.revenue)}</Td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                  <Pagination
                    page={locPage}
                    total={locationRows.length}
                    pageSize={PAGE_SIZE_LOC}
                    onChange={p => setLocPage(p)}
                  />
                </div>
              ) : (
                <EmptyState>
                  No location × referral data yet. Needs sessions with both a geo location and traffic source —
                  use UTM tags on ads (utm_source, utm_medium, utm_campaign) for the clearest ad reporting.
                </EmptyState>
              )}
            </TableCard>

            {/* Recent conversions full-width */}
            <TableCard>
              <ChartTitle>Recent conversions — full journey</ChartTitle>
              <p style={{ margin: '0 0 1rem', fontSize: '0.82rem', color: '#1a3352', opacity: 0.7 }}>
                <strong>First touch</strong> = the source that originally brought this visitor to the site. &nbsp;
                <strong>Last touch</strong> = the source they came from in the session they actually booked. &nbsp;
                <strong>Pages</strong> = pages viewed in the booking session. &nbsp;
                <strong>Visits</strong> = total site visits before converting.
              </p>
              {data.recent_conversions.length > 0 ? (
                <div style={{ overflowX: 'auto' }}>
                  <Table>
                    <thead>
                      <tr>
                        <Th>Booking Ref</Th>
                        <Th>Type</Th>
                        <Th>Value</Th>
                        <Th>Location</Th>
                        <Th>First Touch</Th>
                        <Th>Last Touch</Th>
                        <Th style={{ textAlign: 'center' }}>Pages</Th>
                        <Th style={{ textAlign: 'center' }}>Visits</Th>
                        <Th>Returning?</Th>
                        <Th>Date</Th>
                      </tr>
                    </thead>
                    <tbody>
                      {convPageRows.map((c, i) => {
                        const firstSrc = c.first_source || 'direct';
                        const lastSrc  = c.last_source  || 'direct';
                        const sameTouch = firstSrc === lastSrc && (c.first_medium || '') === (c.last_medium || '');
                        return (
                          <tr key={i}>
                            <Td style={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>{c.booking_reference}</Td>
                            <Td>
                              <TypeBadge type={c.type}>
                                {c.type === 'membership_signup' ? 'Membership' : 'Ticket'}
                              </TypeBadge>
                            </Td>
                            <Td style={{ fontWeight: 600 }}>{c.value ? formatCurrency(c.value) : '—'}</Td>
                            <Td style={{ fontSize: '0.82rem', whiteSpace: 'nowrap' }}>
                              {c.city
                                ? <>
                                    {c.city}
                                    {c.region && c.region !== c.city && (
                                      <div style={{ fontSize: '0.74rem', opacity: 0.6 }}>{c.region}</div>
                                    )}
                                  </>
                                : '—'}
                            </Td>
                            <Td>
                              <span style={{ fontSize: '0.82rem' }}>
                                {firstSrc}{c.first_medium ? ` / ${c.first_medium}` : ''}
                              </span>
                              {c.first_campaign && (
                                <div style={{ fontSize: '0.74rem', opacity: 0.75, fontWeight: 600 }}>{c.first_campaign}</div>
                              )}
                              {c.first_campaign_id && (
                                <div style={{ fontSize: '0.68rem', opacity: 0.45, fontFamily: 'monospace' }}>{c.first_campaign_id}</div>
                              )}
                              {c.first_landing && (
                                <div style={{ fontSize: '0.72rem', opacity: 0.5, fontFamily: 'monospace' }}>{c.first_landing}</div>
                              )}
                            </Td>
                            <Td>
                              {sameTouch ? (
                                <span style={{ fontSize: '0.82rem', opacity: 0.5 }}>same as first</span>
                              ) : (
                                <span style={{ fontSize: '0.82rem' }}>
                                  {lastSrc}{c.last_medium ? ` / ${c.last_medium}` : ''}
                                  {c.last_campaign && (
                                    <div style={{ fontSize: '0.74rem', opacity: 0.75, fontWeight: 600 }}>{c.last_campaign}</div>
                                  )}
                                  {c.last_campaign_id && (
                                    <div style={{ fontSize: '0.68rem', opacity: 0.45, fontFamily: 'monospace' }}>{c.last_campaign_id}</div>
                                  )}
                                </span>
                              )}
                            </Td>
                            <Td style={{ textAlign: 'center', fontWeight: 600 }}>{c.pages_in_session ?? '—'}</Td>
                            <Td style={{ textAlign: 'center', fontWeight: 600 }}>{c.sessions_total ?? '—'}</Td>
                            <Td>
                              {c.is_returning == null ? '—' : (
                                <TypeBadge type={c.is_returning == 1 ? 'membership_signup' : 'ticket_booking'}>
                                  {c.is_returning == 1 ? 'Return' : 'New'}
                                </TypeBadge>
                              )}
                            </Td>
                            <Td style={{ whiteSpace: 'nowrap' }}>{formatDate(c.converted_at)}</Td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </Table>
                  <Pagination
                    page={convPage}
                    total={data.recent_conversions.length}
                    pageSize={PAGE_SIZE_CONV}
                    onChange={p => setConvPage(p)}
                  />
                </div>
              ) : (
                <EmptyState>No conversions recorded yet — they will appear here after the first confirmed booking.</EmptyState>
              )}
            </TableCard>

            {/* ── Auto-tracked campaigns (first-party) ──────────────────────── */}
            <TableCard>
              <ChartTitle>Campaign performance (automatic)</ChartTitle>
              <p style={{ margin: '0 0 1rem', fontSize: '0.82rem', color: '#1a3352', opacity: 0.7 }}>
                Tracked automatically from ad clicks on the website — no manual entry.
                <strong> Last-touch purchases</strong> = bought in a session that came from this campaign.
                <strong> First-touch purchases</strong> = first arrived via this campaign (even if they came back days/weeks later and bought via direct or another channel).
                <strong> Returned &amp; bought</strong> = first-touch from the campaign, then purchased on a later visit.
                Check Meta Ads Manager for spend, CPM and platform ROAS. Figures cover the <strong>{days}-day</strong> period above.
              </p>

              {allCampaigns.length > 0 && (
                <ToolbarRow style={{ marginBottom: '1rem', justifyContent: 'flex-start' }}>
                  <FormInput
                    style={{ maxWidth: 300, marginBottom: 0 }}
                    placeholder="Search campaigns…"
                    value={campSearch}
                    onChange={e => { setCampSearch(e.target.value); setCampPage(0); }}
                  />
                </ToolbarRow>
              )}

              {filteredCampaigns.length > 0 ? (
                <div style={{ overflowX: 'auto' }}>
                  <Table>
                    <thead>
                      <tr>
                        <Th>Campaign</Th>
                        <Th style={{ textAlign: 'right' }}>Sessions</Th>
                        <Th style={{ textAlign: 'right' }}>Visitors</Th>
                        <Th style={{ textAlign: 'right' }}>Last-touch purchases</Th>
                        <Th style={{ textAlign: 'right' }}>Last-touch revenue</Th>
                        <Th style={{ textAlign: 'right' }}>First-touch purchases</Th>
                        <Th style={{ textAlign: 'right' }}>First-touch revenue</Th>
                        <Th style={{ textAlign: 'right' }}>Returned &amp; bought</Th>
                        <Th style={{ textAlign: 'right' }}>Returned revenue</Th>
                        <Th style={{ textAlign: 'right' }}>Tickets</Th>
                        <Th style={{ textAlign: 'right' }}>Memberships</Th>
                      </tr>
                    </thead>
                    <tbody>
                      {campPageRows.map((camp) => {
                        const hasActivity = (camp.sessions || 0) > 0
                          || (camp.first_touch_conversions || 0) > 0
                          || (camp.last_touch_conversions || 0) > 0;
                        return (
                          <tr key={camp.utm_campaign || camp.campaign_id} style={{ opacity: hasActivity ? 1 : 0.4 }}>
                            <Td>
                              <div style={{ fontWeight: 700, fontSize: '0.88rem' }}>{camp.name}</div>
                              <div style={{ fontSize: '0.72rem', opacity: 0.55, fontFamily: 'monospace', marginTop: 2 }}>
                                {camp.campaign_id || camp.utm_campaign}
                              </div>
                            </Td>
                            <Td style={{ textAlign: 'right' }}>{Number(camp.sessions || 0).toLocaleString()}</Td>
                            <Td style={{ textAlign: 'right' }}>{Number(camp.visitors || 0).toLocaleString()}</Td>
                            <Td style={{ textAlign: 'right', fontWeight: 700 }}>
                              {Number(camp.last_touch_conversions || 0).toLocaleString()}
                            </Td>
                            <Td style={{ textAlign: 'right' }}>{formatCurrency(camp.last_touch_revenue || 0)}</Td>
                            <Td style={{ textAlign: 'right', fontWeight: 700, color: '#101d40' }}>
                              {Number(camp.first_touch_conversions || 0).toLocaleString()}
                            </Td>
                            <Td style={{ textAlign: 'right' }}>{formatCurrency(camp.first_touch_revenue || 0)}</Td>
                            <Td style={{ textAlign: 'right', fontWeight: 700, color: (camp.delayed_conversions || 0) > 0 ? '#2d7a4f' : undefined }}>
                              {Number(camp.delayed_conversions || 0).toLocaleString()}
                            </Td>
                            <Td style={{ textAlign: 'right' }}>{formatCurrency(camp.delayed_revenue || 0)}</Td>
                            <Td style={{ textAlign: 'right' }}>{Number(camp.last_touch_tickets || 0).toLocaleString()}</Td>
                            <Td style={{ textAlign: 'right' }}>{Number(camp.last_touch_memberships || 0).toLocaleString()}</Td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </Table>
                  <Pagination
                    page={campPage}
                    total={filteredCampaigns.length}
                    pageSize={PAGE_SIZE_CAMP}
                    onChange={p => setCampPage(p)}
                  />
                </div>
              ) : (
                <EmptyState>
                  {allCampaigns.length === 0
                    ? 'No campaign traffic yet. As soon as someone lands from an ad with utm_campaign set, it will appear here automatically.'
                    : 'No campaigns match your search.'}
                </EmptyState>
              )}
            </TableCard>
          </ChartsGrid>
        </>
      )}

      {!loading && !error && !data && (
        <EmptyState>Select a date range above to load analytics data.</EmptyState>
      )}
    </Wrapper>
  );
};

export default MarketingAnalytics;
